import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { X, ExternalLink, Megaphone, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdStrip — continuously-scrolling promotional ticker (rolling banners).
 * - placement="top": WIDE rich banner supporting photo or video + text + CTA
 * - placement="bottom": compact text-only ticker
 * The track is duplicated so the loop is seamless. Dismissable for 24h via localStorage.
 */

function mediaUrl(path) {
  if (!path) return null;
  return path.startsWith("http") ? path : `${API}/files/${path}`;
}

function RichAdCard({ ad }) {
  const media = mediaUrl(ad.image_path);
  return (
    <a
      href={`${API}/ads/${ad.id}/click`}
      target="_blank" rel="noopener noreferrer"
      onClick={() => { axios.get(`${API}/ads/${ad.id}/click`).catch(() => {}); }}
      data-testid={`ad-card-${ad.id}`}
      className="group flex h-[120px] w-[420px] flex-shrink-0 overflow-hidden rounded-2xl bg-white/95 shadow-lg transition hover:shadow-xl"
    >
      <div className="relative h-full w-[160px] flex-shrink-0 overflow-hidden" style={{ background: ad.accent_color || "#C85A17" }}>
        {ad.video_url ? (
          <video
            src={ad.video_url}
            autoPlay muted loop playsInline
            className="h-full w-full object-cover"
          />
        ) : media ? (
          <img src={media} alt={ad.advertiser} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-white/80">
            <Megaphone className="h-8 w-8" />
          </div>
        )}
        <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">Ad</span>
        {ad.video_url && (
          <span className="absolute bottom-2 right-2 rounded-full bg-black/60 p-1 text-white">
            <Play className="h-3 w-3" fill="currentColor" />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col justify-between p-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: ad.accent_color || "#C85A17" }}>
            {ad.advertiser}
          </div>
          <div className="mt-1 font-display text-sm font-bold leading-tight text-slate-900 line-clamp-2">
            {ad.headline}
          </div>
          {ad.body && <div className="mt-1 text-xs text-slate-600 line-clamp-2">{ad.body}</div>}
        </div>
        <div className="flex items-center gap-1 text-xs font-bold" style={{ color: ad.accent_color || "#C85A17" }}>
          {ad.cta_text || "Learn more"} <ExternalLink className="h-3 w-3" />
        </div>
      </div>
    </a>
  );
}

function CompactAdItem({ ad }) {
  return (
    <a
      href={`${API}/ads/${ad.id}/click`}
      target="_blank" rel="noopener noreferrer"
      onClick={() => { axios.get(`${API}/ads/${ad.id}/click`).catch(() => {}); }}
      data-testid={`ad-item-${ad.id}`}
      className="group flex flex-shrink-0 items-center gap-3 text-sm text-white transition hover:text-copper"
    >
      <span className="inline-block h-2 w-2 flex-shrink-0 rounded-full" style={{ background: ad.accent_color || "#C85A17" }} />
      <span className="font-semibold text-white/70">{ad.advertiser} ·</span>
      <span className="font-semibold text-white">{ad.headline}</span>
      {ad.body && <span className="hidden text-white/70 md:inline">— {ad.body}</span>}
      <span
        className="ml-1 inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-[11px] font-bold text-white group-hover:brightness-110"
        style={{ background: ad.accent_color || "#C85A17" }}
      >
        {ad.cta_text || "Learn more"} <ExternalLink className="h-3 w-3" />
      </span>
    </a>
  );
}

export default function AdStrip({ placement = "top" }) {
  const rich = placement === "top";
  const [ads, setAds] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    const t = localStorage.getItem(`easystay_ad_dismissed_${placement}`);
    return t && Date.now() - parseInt(t) < 24 * 60 * 60 * 1000;
  });
  const impressedRef = useRef(new Set());
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(50);

  useEffect(() => {
    axios.get(`${API}/ads`, { params: { placement } })
      .then(({ data }) => setAds(data.filter((a) => a.is_active)))
      .catch(() => {});
  }, [placement]);

  useEffect(() => {
    ads.forEach((ad) => {
      if (!impressedRef.current.has(ad.id)) {
        impressedRef.current.add(ad.id);
        axios.post(`${API}/ads/${ad.id}/impression`).catch(() => {});
      }
    });
  }, [ads]);

  useEffect(() => {
    if (!trackRef.current) return;
    const width = trackRef.current.scrollWidth / 2;
    const pxPerSec = rich ? 45 : 60;
    setDuration(Math.max(25, Math.round(width / pxPerSec)));
  }, [ads, rich]);

  if (dismissed || ads.length === 0) return null;

  const dismiss = () => {
    localStorage.setItem(`easystay_ad_dismissed_${placement}`, String(Date.now()));
    setDismissed(true);
  };

  const roll = [...ads, ...ads];
  const containerCls = placement === "bottom"
    ? "fixed bottom-0 left-0 right-0 z-40"
    : "sticky top-0 z-40";

  return (
    <div data-testid={`ad-strip-${placement}`} className={containerCls}>
      <div className={`relative overflow-hidden border-b border-black/10 ${rich ? "bg-forest" : "bg-forest"}`}>
        <div className={`absolute left-0 right-0 h-[2px] bg-copper ${placement === "bottom" ? "bottom-0" : "top-0"}`} />

        <div className={`mx-auto flex items-center gap-3 ${rich ? "px-4 py-3 sm:px-6" : "px-3 py-2 sm:px-6 min-h-[44px] sm:min-h-[44px]"}`}>
          <span className="hidden flex-shrink-0 items-center gap-1.5 rounded-full bg-copper/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white sm:inline-flex">
            <Megaphone className="h-3 w-3" /> Sponsored
          </span>

          <div className="relative flex-1 overflow-hidden">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-forest to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-forest to-transparent" />

            <div
              ref={trackRef}
              className={`marquee-track flex items-center ${rich ? "gap-4" : "gap-12"} whitespace-nowrap`}
              style={{ animationDuration: `${duration}s` }}
            >
              {roll.map((ad, i) =>
                rich
                  ? <RichAdCard key={`${ad.id}-${i}`} ad={ad} />
                  : <CompactAdItem key={`${ad.id}-${i}`} ad={ad} />
              )}
            </div>
          </div>

          <button
            onClick={dismiss}
            data-testid={`ad-close-${placement}`}
            className="flex-shrink-0 rounded-full p-1 text-white/70 hover:bg-white/10 hover:text-white"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
